import React, { useEffect, useRef, useState } from 'react';
import { daysBetween, dueLabel, durationLabel, isoDay, longDay, repeatText } from '../../lib/quickAdd';
import { PRIORITIES } from './TodoEditor';
import { AiBadge } from './VoiceTodo';
import { Icon } from './icons';
import './TodoAssist.css';
import './VoiceDrafts.css';

/*
 * THE PREVIEW OF EVERYTHING ONE SENTENCE MAKES (23 October).
 *
 * Gordon said "Next week I want to do the staff reviews on Tuesday and
 * Wednesday from 2pm to 5pm" and got one to-do. Now the sentence can be two -
 * or ten - and this shows them all, as a compact list, before anything is
 * made: each with its title, day, time, how long, list and who has it, a
 * tick to keep it, a cross to drop it, and "More" for priority, notes,
 * deadline and labels. What was heard is one line underneath; the button
 * says how many it will make. Never made silently.
 *
 * KEYBOARD: Tab moves through the rows; Enter in a title creates the ticked
 * ones; Esc anywhere cancels. On a phone each row's fields wrap under its
 * title, and the buttons fill the width.
 *
 * `drafts` are lib/voiceDraft's shape; `onCreate(kept)` gets the ticked
 * ones, in order, as edited.
 */

const DURATIONS = [15, 30, 45, 60, 90, 120, 180, 240, 480];
const plural = (n, one, many) => (n === 1 ? one : many);
const hhmm = (m) => `${String(Math.floor(m / 60) % 24).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;

/**
 * The day and time a row is for, said so it can be checked at a glance:
 * "Tomorrow, Wed 21 Oct · 14:00-17:00" - the weekday always, because the
 * date field does not show it and the weekday is what was said.
 */
export function whenWords(date, time, minutes, now) {
  if (!date) return time || '';
  const n = now ? daysBetween(isoDay(now), date) : null;
  const rel = n === 0 ? 'Today, ' : n === 1 ? 'Tomorrow, ' : '';
  let t = '';
  if (time) {
    const [h, m] = time.split(':').map(Number);
    t = minutes ? ` · ${time}–${hhmm(h * 60 + m + minutes)}` : ` · ${time}`;
  }
  return `${rel}${longDay(date)}${t}`;
}

export function VoiceDraftList({ drafts: initial, heard, typed = false, languageName = null, note = null, lists = [], labels = [],
                                 membersOf, defaultListId, now, plan = false, busy = false, error = null,
                                 onCreate, onCancel, onAgain = null }) {
  const [rows, setRows] = useState(() => initial.map((d, i) => ({ ...d, rid: i, on: true, open: false })));
  useEffect(() => { setRows(initial.map((d, i) => ({ ...d, rid: i, on: true, open: false }))); }, [initial]);
  const first = useRef(null);
  useEffect(() => { if (first.current) first.current.focus({ preventScroll: true }); }, []);
  const patch = (rid, change) => setRows((rs) => rs.map((r) => (r.rid === rid ? { ...r, ...change } : r)));
  const kept = rows.filter((r) => r.on && r.title.trim());
  const create = () => {
    if (busy || !kept.length) return;
    onCreate(kept.map(({ rid, on, open, ...d }) => ({ ...d, title: d.title.trim() })));
  };
  const source = rows.some((r) => r.source === 'ai') ? 'ai' : 'quick';
  const activeLists = lists.filter((l) => !l.archived_at);
  const what = typed ? 'from what you typed' : 'from your voice';
  const count = rows.length;

  return (
    <div className="tva-card tvl" role="region" aria-label={`New to-dos ${what}`} data-voice-draft={source} data-voice-count={count}
         onKeyDown={(e) => { if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); onCancel(); } }}>
      <div className="tva-card-head">
        {source === 'ai' ? <AiBadge /> : <span className="tva-quick" title="Read by quick add's own rules">Quick add</span>}
        <span className="tva-card-title">{count} {plural(count, 'to-do', 'to-dos')} {what}</span>
        <button type="button" className="tds-icon-btn" aria-label="Cancel" title="Cancel (Esc)" onClick={onCancel}><Icon name="close" size={15} /></button>
      </div>
      {note && <p className="tva-note" role="status">{note}</p>}
      {error && <p className="tds-error" role="alert">{error}</p>}

      <ol className="tvl-list">
        {rows.map((r, i) => {
          const listId = r.list_id || defaultListId;
          const list = activeLists.find((l) => l.id === listId) || null;
          const members = membersOf ? membersOf(listId) : [];
          const member = !r.assignee_key || members.some((m) => m.key === r.assignee_key);
          const labelOf = (id) => labels.find((l) => l.id === id);
          return (
            <li key={r.rid} className={`tvl-row${r.on ? '' : ' is-off'}`} data-voice-row={i}>
              <div className="tvl-top">
                <input type="checkbox" className="tvl-check" checked={r.on} aria-label={`Include "${r.title || `to-do ${i + 1}`}"`}
                       onChange={(e) => patch(r.rid, { on: e.target.checked })} />
                <input ref={i === 0 ? first : null} type="text" className="tvl-title" value={r.title} maxLength={300}
                       aria-label={`To-do ${i + 1}`}
                       onChange={(e) => patch(r.rid, { title: e.target.value })}
                       onKeyDown={(e) => { if (e.key === 'Enter' && !e.nativeEvent.isComposing) { e.preventDefault(); create(); } }} />
                <button type="button" className="tds-icon-btn tvl-remove" aria-label={`Remove "${r.title || `to-do ${i + 1}`}"`}
                        title="Remove this one" onClick={() => setRows((rs) => rs.filter((x) => x.rid !== r.rid))}>
                  <Icon name="close" size={14} />
                </button>
              </div>
              <div className="tvl-fields">
                <label className="tvl-field"><span>Day</span>
                  <input type="date" value={r.due_date || ''} aria-label={`Day of to-do ${i + 1}`}
                         onChange={(e) => patch(r.rid, { due_date: e.target.value || null })} />
                </label>
                <label className="tvl-field is-time"><span>Time</span>
                  <input type="time" value={r.due_time || ''} aria-label={`Time of to-do ${i + 1}`}
                         onChange={(e) => patch(r.rid, { due_time: e.target.value || null })} />
                </label>
                {plan && (
                  <label className="tvl-field"><span>Takes</span>
                    <select value={r.duration_minutes || ''} aria-label={`How long to-do ${i + 1} takes`}
                            onChange={(e) => patch(r.rid, { duration_minutes: Number(e.target.value) || null })}>
                      <option value="">—</option>
                      {[...new Set([...DURATIONS, ...(r.duration_minutes ? [r.duration_minutes] : [])])].sort((a, b) => a - b)
                        .map((m) => <option key={m} value={m}>{durationLabel(m)}</option>)}
                    </select>
                  </label>
                )}
                <label className="tvl-field is-list"><span>List</span>
                  <select value={listId || ''} aria-label={`List of to-do ${i + 1}`} onChange={(e) => patch(r.rid, { list_id: e.target.value || null })}>
                    {activeLists.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
                  </select>
                </label>
                <label className="tvl-field is-who"><span>For</span>
                  <select value={r.assignee_key || ''} aria-label={`Who has to-do ${i + 1}`} onChange={(e) => {
                    const k = e.target.value || null;
                    const who = members.find((m) => m.key === k);
                    patch(r.rid, { assignee_key: k, assignee_name: who ? who.name : (k === r.assignee_key ? r.assignee_name : null) });
                  }}>
                    <option value="">Me / nobody</option>
                    {members.map((m) => <option key={m.key} value={m.key}>{m.name}</option>)}
                    {r.assignee_key && !member && <option value={r.assignee_key}>{r.assignee_name} (send to their Inbox)</option>}
                  </select>
                </label>
              </div>
              <div className="tva-chips tvl-chips">
                {r.due_date && <span className="ctd-chip is-date" data-chip="date"><Icon name="calendar" size={12} />{whenWords(r.due_date, r.due_time, plan ? r.duration_minutes : null, now)}</span>}
                {r.repeat && <span className="ctd-chip is-repeat" data-chip="repeat">↻ {repeatText(r.repeat, { now, dueDate: r.due_date })}</span>}
                {plan && r.deadline && <span className="ctd-chip is-deadline" data-chip="deadline"><Icon name="deadline" size={12} />By {dueLabel(r.deadline, null, now)}</span>}
                {r.reminder_minutes_before !== null && r.reminder_minutes_before !== undefined && (
                  <span className="ctd-chip is-reminder" data-chip="reminder"><Icon name="bell" size={12} />
                    {r.reminder_minutes_before === 0 ? 'At the time' : `${durationLabel(r.reminder_minutes_before)} before`}</span>
                )}
                {r.priority < 4 && <span className={`ctd-chip is-priority p${r.priority}`} data-chip="priority"><Icon name="flag" size={12} />P{r.priority}</span>}
                {(r.label_ids || []).map((id) => labelOf(id) && (
                  <span key={id} className="ctd-chip is-label" data-chip="label"><Icon name="tag" size={12} />{labelOf(id).name}</span>
                ))}
                {list && list.id !== defaultListId && <span className="ctd-chip is-list" data-chip="list"><Icon name="hash" size={12} />{list.name}</span>}
                <button type="button" className="tds-link tvl-more" aria-expanded={r.open} onClick={() => patch(r.rid, { open: !r.open })}>
                  {r.open ? 'Less' : 'More'}
                </button>
              </div>
              {(r.heads_up || []).map((h) => <p key={h} className="tva-note tvl-heads" data-voice-heads="">{h}</p>)}
              {r.open && (
                <div className="tva-fields tvl-extra">
                  <label className="tds-field"><span>Priority</span>
                    <select value={String(r.priority)} onChange={(e) => patch(r.rid, { priority: Number(e.target.value) })}>
                      {PRIORITIES.map((p) => <option key={p.value} value={String(p.value)}>{p.label}</option>)}
                    </select>
                  </label>
                  {plan && (
                    <label className="tds-field"><span>Deadline</span>
                      <input type="date" value={r.deadline || ''} onChange={(e) => patch(r.rid, { deadline: e.target.value || null })} />
                    </label>
                  )}
                  <label className="tds-field tva-wide"><span>Notes</span>
                    <textarea rows={2} value={r.notes || ''} maxLength={4000} onChange={(e) => patch(r.rid, { notes: e.target.value || null })} />
                  </label>
                  {labels.length > 0 && (
                    <fieldset className="tva-labels tva-wide">
                      <legend>Labels</legend>
                      {labels.map((l) => (
                        <label key={l.id} className={`tva-label${(r.label_ids || []).includes(l.id) ? ' is-on' : ''}`}>
                          <input type="checkbox" checked={(r.label_ids || []).includes(l.id)}
                                 onChange={(e) => patch(r.rid, { label_ids: e.target.checked ? [...(r.label_ids || []), l.id] : (r.label_ids || []).filter((x) => x !== l.id) })} />
                          {l.name}
                        </label>
                      ))}
                    </fieldset>
                  )}
                </div>
              )}
            </li>
          );
        })}
      </ol>
      {!rows.length && <p className="tds-quiet">All removed. Cancel, or say it again.</p>}

      <p className="tva-heard" data-voice-transcript="">
        <span className="tds-quiet">{typed ? 'Typed' : 'Heard'}{languageName ? ` (${String(languageName).replace(/^Auto \((.*)\)$/, '$1')})` : ''}:</span> <q>{heard}</q>
        {onAgain && <button type="button" className="tds-link" onClick={onAgain}>Say it again</button>}
      </p>
      <div className="tva-card-foot">
        <span className="tva-spacer" />
        <button type="button" className="btn btn-outline tds-btn" onClick={onCancel}>Cancel</button>
        <button type="button" className="btn btn-primary tds-btn" data-voice-create="" disabled={busy || !kept.length} onClick={create}>
          {kept.length === 1 ? 'Create to-do' : `Create ${kept.length} to-dos`}
        </button>
      </div>
    </div>
  );
}

export default VoiceDraftList;
